/**
 * /customer/brand/:id — single-brand detail surface.
 *
 * Sections (top to bottom):
 *   • Brand banner + logo + name + description
 *   • "Your progress" card (only shown when the customer holds a card
 *     for this brand)
 *   • Available rewards (read-only list this milestone)
 *   • Locations (branch list with Open Maps + Call CTAs — basic; W-4
 *     ships the polished surface)
 *   • Promotions placeholder (wired in W-2)
 */
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, MapPin, Phone, Gift, Sparkles, Plus,
  Loader2, Megaphone, Receipt,
} from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { toast } from 'sonner';

export default function CustomerBrandDetailPage() {
  const { id: brandId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const queryClient = useQueryClient();

  const brandQ = useQuery({
    queryKey: qk.customerBrand(brandId),
    queryFn: () => api.customerBrand(token, brandId),
    enabled: !!token && !!brandId,
  });
  const branchesQ = useQuery({
    queryKey: qk.customerBrandBranches(brandId),
    queryFn: () => api.customerBrandBranches(token, brandId),
    enabled: !!token && !!brandId,
  });
  const rewardsQ = useQuery({
    queryKey: qk.customerBrandRewards(brandId),
    queryFn: () => api.customerBrandRewards(token, brandId),
    enabled: !!token && !!brandId,
  });
  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
  });

  const joined = (cardsQ.data ?? []).find((c) => c.brand?.id === brandId);

  const joinMut = useMutation({
    mutationFn: () => api.customerJoinCard(token, brandId),
    onSuccess: () => {
      toast.success(`Joined ${brandQ.data?.name ?? 'brand'} — start collecting stamps!`);
      queryClient.invalidateQueries({ queryKey: qk.customerCards() });
    },
    onError: (e) => toast.error(e?.detail ?? 'Could not join brand'),
  });

  if (brandQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="animate-spin text-gold" />
      </div>
    );
  }
  if (brandQ.error || !brandQ.data) {
    return (
      <div className="text-center py-24 text-white/55">
        <div className="text-base font-medium mb-1">Brand not found</div>
        <Link to="/customer/home" className="text-gold text-sm hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  const b = brandQ.data;
  const next = joined?.next_reward_at ?? b.stamps_per_card ?? 10;
  const pct = joined ? Math.min(100, ((joined.current_stamps ?? 0) / next) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="customer-brand-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
      >
        <ChevronLeft size={14} /> Back
      </button>

      {/* Banner */}
      <div
        className="relative h-44 md:h-56 rounded-3xl overflow-hidden bg-gradient-to-br from-gold/15 to-black/60"
        data-testid="customer-brand-banner"
        style={b.banner_url ? { backgroundImage: `url(${resolveImageUrl(b.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : null}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
          <div className="flex items-end gap-4 min-w-0">
            {b.logo_url ? (
              <img
                src={resolveImageUrl(b.logo_url)}
                alt=""
                className="w-16 h-16 rounded-2xl border border-white/15 shadow-2xl bg-black/40 shrink-0"
              />
            ) : null}
            <div className="min-w-0">
              <h1 className="font-display text-2xl md:text-3xl font-bold truncate">
                {b.name}
              </h1>
              {b.description ? (
                <div className="text-sm text-white/70 truncate mt-0.5">{b.description}</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Your progress OR Join CTA */}
      {joined ? (
        <section data-testid="customer-brand-progress" className="rounded-3xl border border-gold/25 bg-gold/5 p-6 shadow-glow-gold-sm">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-gold font-mono">
              Your progress
            </div>
            <div className="font-mono text-lg font-semibold text-gold">
              {joined.current_stamps ?? 0} / {next}
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-white/55 mt-3">
            {(next - (joined.current_stamps ?? 0))} more stamp
            {next - (joined.current_stamps ?? 0) === 1 ? '' : 's'} to unlock your next reward.
          </div>
          {/* Phase R3 — Receipt OCR Claim CTA. Available to joined
              customers; the backend's rate-limit guards prevent abuse. */}
          <button
            type="button"
            onClick={() => navigate(`/customer/claim-stamp/${brandId}`)}
            data-testid="customer-brand-claim-stamp"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3 text-sm hover:bg-gold-light transition-colors shadow-glow-gold-sm min-h-[44px]"
          >
            <Receipt size={16} />
            Claim Stamp from Receipt
          </button>
        </section>
      ) : (
        <section className="rounded-3xl border border-white/8 bg-[#15151B] p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-medium text-sm">Not collecting stamps yet</div>
            <div className="text-xs text-white/55 mt-1">
              Join {b.name} to start earning toward rewards.
            </div>
          </div>
          <button
            onClick={() => joinMut.mutate()}
            disabled={joinMut.isPending}
            data-testid="customer-brand-join"
            className="inline-flex items-center gap-2 bg-gold text-black font-semibold rounded-xl px-4 py-2 text-sm hover:bg-gold-light disabled:opacity-50 transition-colors shadow-glow-gold-sm"
          >
            {joinMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Join brand
          </button>
        </section>
      )}

      {/* Rewards */}
      <section data-testid="customer-brand-rewards">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Available rewards</h2>
          <Gift size={14} className="text-gold" />
        </div>
        {rewardsQ.isLoading ? (
          <div className="h-24 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (rewardsQ.data ?? []).length === 0 ? (
          <div className="rounded-2xl border border-white/8 bg-[#15151B] p-6 text-center text-sm text-white/40">
            No rewards configured yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(rewardsQ.data ?? []).map((r) => (
              <div
                key={r.id}
                data-testid={`customer-reward-${r.id}`}
                className="rounded-2xl border border-white/8 bg-[#15151B] p-4 flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold shrink-0">
                  <Gift size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{r.title}</div>
                  {r.description ? (
                    <div className="text-xs text-white/45 truncate mt-0.5">{r.description}</div>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm font-semibold text-gold">
                    {r.stamps_required}
                  </div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">
                    stamps
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Locations */}
      <section data-testid="customer-brand-locations">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Locations</h2>
          <MapPin size={14} className="text-gold" />
        </div>
        {branchesQ.isLoading ? (
          <div className="h-24 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (branchesQ.data ?? []).length === 0 ? (
          <div className="rounded-2xl border border-white/8 bg-[#15151B] p-6 text-center text-sm text-white/40">
            No branches available.
          </div>
        ) : (
          <div className="space-y-2.5">
            {(branchesQ.data ?? []).map((br) => {
              const hasCoords = br.lat != null && br.lng != null;
              const mapsUrl = hasCoords
                ? `https://www.google.com/maps?q=${br.lat},${br.lng}`
                : `https://www.google.com/maps?q=${encodeURIComponent(`${br.name} ${br.address ?? ''}`)}`;
              const hasPhone = !!br.phone;
              return (
                <div
                  key={br.id}
                  data-testid={`customer-branch-row-${br.id}`}
                  className="rounded-2xl border border-white/8 bg-[#15151B] p-4 space-y-3"
                >
                  {/* W-4: name → address → buttons → other meta. Address is
                      prominent (text-sm white/75) + break-words so long
                      Jakarta addresses never clip on narrow viewports. */}
                  <div className="space-y-1">
                    <div
                      className="font-semibold text-sm text-white"
                      data-testid={`customer-branch-row-${br.id}-name`}
                    >
                      {br.name}
                    </div>
                    {br.address ? (
                      <p
                        className="text-sm text-white/70 break-words leading-snug"
                        data-testid={`customer-branch-row-${br.id}-address`}
                      >
                        {br.address}
                      </p>
                    ) : null}
                  </div>

                  {/* W-4: action buttons. Stacked full-width below ~sm
                      (narrow phones / mobile web), inline auto-width with
                      gap-3 on sm:+ . Min-w on wide ensures both buttons
                      stay at a comfortable 140px tap target. */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <a
                      href={hasCoords ? mapsUrl : undefined}
                      target={hasCoords ? '_blank' : undefined}
                      rel={hasCoords ? 'noreferrer' : undefined}
                      aria-disabled={!hasCoords}
                      title={hasCoords ? 'Open in Google Maps' : 'Location not available'}
                      onClick={(e) => { if (!hasCoords) e.preventDefault(); }}
                      data-testid={`customer-branch-row-${br.id}-maps-btn`}
                      className={
                        'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors w-full sm:w-auto sm:min-w-[140px] ' +
                        (hasCoords
                          ? 'bg-gold/10 border-gold/40 text-gold hover:bg-gold/15 hover:border-gold/55'
                          : 'bg-white/[0.03] border-white/8 text-white/30 cursor-not-allowed pointer-events-none')
                      }
                    >
                      <MapPin size={14} /> Open Maps
                    </a>
                    <a
                      href={hasPhone ? `tel:${br.phone}` : undefined}
                      aria-disabled={!hasPhone}
                      title={hasPhone ? `Call ${br.phone}` : 'Phone not available'}
                      onClick={(e) => { if (!hasPhone) e.preventDefault(); }}
                      data-testid={`customer-branch-row-${br.id}-call-btn`}
                      className={
                        'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors w-full sm:w-auto sm:min-w-[140px] ' +
                        (hasPhone
                          ? 'bg-white/5 border-white/12 text-white/80 hover:bg-gold/10 hover:border-gold/40 hover:text-gold'
                          : 'bg-white/[0.03] border-white/8 text-white/30 cursor-not-allowed pointer-events-none')
                      }
                    >
                      <Phone size={14} /> Call
                    </a>
                  </div>

                  {/* Other meta (opening hours, phone fallback) reserved
                      for a future surface — Phase W-1 scaffold intentionally
                      kept this minimal. */}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Promotions — Phase W-2 wired (brand-scoped) */}
      <BrandPromotionsStrip brandId={brandId} />
    </div>
  );
}

function BrandPromotionsStrip({ brandId }) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const promosQ = useQuery({
    queryKey: qk.customerPromotions({ brand_id: brandId }),
    queryFn: () => api.customerPromotions(token, { brand_id: brandId }),
    enabled: !!token && !!brandId,
  });
  const promos = promosQ.data ?? [];

  // Empty brand promotions: hide the section entirely (no clutter on
  // brands with no live campaigns).
  if (!promosQ.isLoading && promos.length === 0) return null;

  return (
    <section data-testid="customer-brand-promotions-section">
      <h2 className="font-display text-lg font-semibold mb-3">Promotions</h2>
      {promosQ.isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="shrink-0 w-72 h-40 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
          {promos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate(`/customer/promotions/${p.id}`)}
              data-testid={`customer-promo-card-${p.id}`}
              className="relative shrink-0 w-72 h-40 rounded-2xl overflow-hidden border border-white/10 bg-[#15151B] hover:border-gold/35 transition-colors text-left snap-start"
            >
              {p.image_url ? (
                <img
                  src={resolveImageUrl(p.image_url)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              {(p.branch_ids ?? []).length > 0 ? (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/55 border border-white/20 backdrop-blur text-[10px] font-mono uppercase tracking-wider text-white/85">
                  Branch only
                </div>
              ) : null}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="font-display text-base font-semibold leading-tight line-clamp-2">
                  {p.title}
                </div>
                <div className="text-[11px] text-white/65 mt-1 line-clamp-1">
                  {p.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
